import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { videosAPI, purchasesAPI, stripeAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { CommentSection, LikeButton } from '../components/Comments';
import MuxVideoPlayer from '../components/VideoPlayer/MuxVideoPlayer';
import AlertModal from '../components/common/AlertModal';
import BattleVoting from '../components/BattleVoting';
import { buildSides } from '../utils/battleSides';
import MuxThumbnailFramePicker from '../components/AdminDashboard/MuxThumbnailFramePicker';

const VideoDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasPurchased, setHasPurchased] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [alertModal, setAlertModal] = useState({ show: false, title: '', message: '', type: 'info' });

  // Edit panel state
  const [editOpen, setEditOpen] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [editPrice, setEditPrice] = useState('');
  // editFree is kept for backward compatibility with existing tests but is no longer
  // shown as a UI control -- free is now derived from whether a price is set.
  const [editFree, setEditFree] = useState(false); // eslint-disable-line no-unused-vars
  const [editThumbnailTime, setEditThumbnailTime] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  // Guard to ensure recordView is called at most once per mount, even if the
  // video state or canWatch re-renders. Reset when the video id changes (new route).
  const viewRecorded = useRef(false);

  useEffect(() => {
    viewRecorded.current = false;
  }, [id]);

  useEffect(() => {
    fetchVideo();
    // Handle Stripe redirect back from Checkout.
    // ?purchase=success means the user just paid. Set hasPurchased=true optimistically
    // and skip the initial status check -- the webhook may not have fired yet on
    // Render's free tier (cold-start sleep). The StripeReconciler self-heals on the
    // next playback_token request. A false result from checkPurchaseStatus must NOT
    // overwrite this optimistic grant.
    const params = new URLSearchParams(location.search);
    const justPurchased = params.get('purchase') === 'success';
    if (justPurchased) {
      setHasPurchased(true);
      setAlertModal({
        show: true,
        title: 'Purchase Successful',
        message: 'Thank you! Your purchase is confirmed. Enjoy the video.',
        type: 'success',
      });
    } else if (user) {
      checkPurchaseStatus();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user]);

  const fetchVideo = async () => {
    try {
      const response = await videosAPI.getById(id);
      setVideo(response.data);
    } catch (error) {
      console.error('Error fetching video:', error);
      const status = error.response?.status;
      // 401 = not logged in + exclusive (login_required)
      // 402 = logged in + exclusive + no purchase (purchase_required)
      // 403 = legacy fallback (kept for backward compatibility)
      if (status === 401 || status === 402 || status === 403) {
        // Return display metadata so we can render the right CTA gate
        setVideo(error.response.data.video);
      }
    } finally {
      setLoading(false);
    }
  };

  const checkPurchaseStatus = async () => {
    try {
      const response = await purchasesAPI.check(id);
      // Only set hasPurchased from the API when the result is true, or when there
      // is no ?purchase=success flag. This prevents a race where a false result
      // (webhook not yet fired) clobbers the optimistic grant the user just received.
      const params = new URLSearchParams(window.location.search);
      if (response.data.has_purchased || params.get('purchase') !== 'success') {
        setHasPurchased(response.data.has_purchased);
      }
    } catch (error) {
      console.error('Error checking purchase status:', error);
    }
  };

  const handlePurchase = async () => {
    if (!user) {
      setAlertModal({
        show: true,
        title: 'Sign In Required',
        message: 'Please sign in to purchase this video.',
        type: 'info'
      });
      return;
    }

    setPurchasing(true);
    try {
      const response = await stripeAPI.createCheckout(id);
      // Redirect to Stripe Checkout -- browser leaves this page.
      // On return, Stripe appends ?purchase=success to the success_url.
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Purchase error:', error);
      setAlertModal({
        show: true,
        title: 'Purchase Failed',
        message: error.response?.data?.error || 'Failed to start checkout. Please try again.',
        type: 'error',
      });
      setPurchasing(false);
    }
  };

  // Returns true only when the current user is a platform admin.
  // Per owner directive: only admins may edit videos -- artists have no edit access.
  const checkIsAdmin = () => {
    if (!user || !video) return false;
    return user.role === 2 || user.role === 'admin';
  };

  const openEditPanel = () => {
    setEditTitle(video.title || '');
    setEditDescription(video.description || '');
    setEditStatus(video.status || 'public');
    setEditPrice(video.price_cents ? (video.price_cents / 100).toFixed(2) : '');
    setEditFree(video.free || false);
    setEditThumbnailTime(video.thumbnail_time !== undefined ? video.thumbnail_time : null);
    setEditError('');
    setEditOpen(true);
  };

  const handleEditSave = async () => {
    setEditSaving(true);
    setEditError('');
    try {
      // Price is now independent of status -- send it for any status.
      // Blank price = free (no purchase required). Backend derives `free` from price.
      const payload = {
        video: {
          title: editTitle,
          description: editDescription,
          status: editStatus,
          // Send price for any status; blank string clears the price (makes it free).
          price: editPrice,
          // thumbnail_time: null = Mux default frame; a number = chosen frame
          thumbnail_time: editThumbnailTime,
        },
      };
      const response = await videosAPI.update(id, payload);
      setVideo(response.data);
      setEditOpen(false);
    } catch (err) {
      const msgs = err?.response?.data?.errors;
      setEditError(
        Array.isArray(msgs) ? msgs.join(', ') : (err?.response?.data?.error || 'Save failed')
      );
    } finally {
      setEditSaving(false);
    }
  };

  const getEmbedUrl = () => {
    if (!video) return null;

    if (video.platform === 'youtube') {
      return `https://www.youtube.com/embed/${video.platform_video_id}`;
    } else if (video.platform === 'vimeo') {
      return `https://player.vimeo.com/video/${video.platform_video_id}`;
    } else if (video.platform === 'aws') {
      return video.platform_video_id;
    }
    return null;
  };

  // Render VS treatment for artists. Two-sided battles (1v1 and 2v2) show
  // side 1 VS side 2; each side lists its artist(s).
  const renderArtists = () => {
    if (!video?.artists || video.artists.length === 0) return null;

    const sides = buildSides(video.artists);
    const twoSided = sides[0].artists.length > 0 && sides[1].artists.length > 0;

    if (twoSided) {
      const renderSide = (side) => (
        <div className="flex flex-col items-center gap-1">
          {side.artists.map((a) => (
            <button
              key={a.id}
              onClick={() => navigate(`/artist/${a.id}`)}
              className="group flex items-center space-x-2 hover:opacity-80 transition-opacity"
              data-testid={`artist-link-${a.id}`}
            >
              {a.avatar_url && (
                <img src={a.avatar_url} alt={a.display_name || a.username} className="w-8 h-8 rounded-full" />
              )}
              <span className="text-white font-bold text-lg uppercase tracking-tight group-hover:text-brand-yellow transition-colors">
                {a.display_name || a.username}
              </span>
            </button>
          ))}
        </div>
      );

      return (
        <div className="mb-6">
          <div className="flex items-center justify-center gap-4 py-3">
            {renderSide(sides[0])}

            <span
              className="text-brand-yellow font-black text-xl px-2 py-0.5 rounded"
              style={{
                border: '1px solid rgba(245,230,66,0.30)',
                background: 'rgba(245,230,66,0.08)',
              }}
            >
              VS
            </span>

            {renderSide(sides[1])}
          </div>
        </div>
      );
    }

    // Fallback for 1 or 3+ artists
    return (
      <div className="mb-6">
        <h3 className="text-gray-500 text-xs uppercase tracking-widest mb-3">Artists</h3>
        <div className="flex flex-wrap gap-3">
          {video.artists.map((artist) => (
            <button
              key={artist.id}
              onClick={() => navigate(`/artist/${artist.id}`)}
              className="flex items-center space-x-2 bg-battle-dark px-4 py-2 rounded-lg border border-army/30 hover:border-brand-yellow/40 transition-all duration-200"
              data-testid={`artist-link-${artist.id}`}
            >
              {artist.avatar_url && (
                <img src={artist.avatar_url} alt={artist.display_name || artist.username} className="w-8 h-8 rounded-full" />
              )}
              <span className="text-white font-semibold">{artist.display_name || artist.username}</span>
            </button>
          ))}
        </div>
      </div>
    );
  };

  // Record a view for non-YouTube videos when the user has access.
  // Runs once per mount (guarded by viewRecorded ref). YouTube has its own view
  // counting mechanism so we never call recordView for youtube platform videos.
  useEffect(() => {
    if (!video) return;
    if (video.platform === 'youtube') return;
    if (viewRecorded.current) return;

    // Recompute canWatch inline so the effect can run before the render-path
    // const declarations (which live after the early returns).
    const _isExclusive = video.status === 'exclusive';
    const _requiresPurchase = video.requires_purchase !== undefined
      ? video.requires_purchase
      : (_isExclusive && !video.free);
    const _requiresLogin = video.requires_login !== undefined
      ? video.requires_login
      : _isExclusive;
    const _isAdmin = user?.role === 'admin' || user?.role === 2;
    const _canWatch = _isAdmin
      || (_requiresPurchase && hasPurchased)
      || (_requiresLogin && !_requiresPurchase && !!user)
      || !_requiresLogin;

    if (!_canWatch) return;

    viewRecorded.current = true;
    Promise.resolve(videosAPI.recordView(id)).catch(() => {
      // Fire-and-forget -- view count is non-critical; silently ignore errors
    });
  }, [video, hasPurchased, user, id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        {/* battle-red kept: live activity spinner = semantic */}
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-battle-red"></div>
      </div>
    );
  }

  if (!video) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-2">Video not found</h2>
          <p className="text-gray-500">This battle may have been removed.</p>
        </div>
      </div>
    );
  }

  const isExclusive = video.status === 'exclusive';
  // Derive gate flags from new API fields, fall back to legacy `free` + status for
  // backward-compatibility with older serialized responses.
  const requiresPurchase = video.requires_purchase !== undefined
    ? video.requires_purchase
    : (isExclusive && !video.free);
  const requiresLogin = video.requires_login !== undefined
    ? video.requires_login
    : (isExclusive);
  const isFreeExclusive = isExclusive && !requiresPurchase;

  // canWatch truth table:
  // - admin: always
  // - requires_purchase + hasPurchased: allow
  // - requires_login + logged in (not requires_purchase): allow (free exclusive)
  // - !requires_login: allow (free public / free unlisted)
  const isAdmin = user?.role === 'admin' || user?.role === 2;
  const canWatch = isAdmin
    || (requiresPurchase && hasPurchased)
    || (requiresLogin && !requiresPurchase && !!user)
    || !requiresLogin;

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="text-gray-500 hover:text-white mb-6 flex items-center group transition-colors"
          data-testid="back-button"
        >
          <svg className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>

        {/* Video Player */}
        <div className="rounded-xl overflow-hidden shadow-2xl mb-8">
          {canWatch ? (
            video.platform === 'mux' ? (
              <MuxVideoPlayer video={video} />
            ) : (
              <div className="aspect-video bg-black">
                <iframe
                  src={getEmbedUrl()}
                  title={video.title}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  data-testid="video-player"
                />
              </div>
            )
          ) : (
            <div className="aspect-video flex flex-col items-center justify-center bg-battle-gray relative overflow-hidden">
              {/* Dramatic background */}
              <div className="absolute inset-0 bg-gradient-to-b from-battle-dark/50 via-transparent to-battle-dark/80" />
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(74,93,58,0.12)_0%,transparent_70%)]" />

              <div className="relative z-10 text-center px-6">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-battle-dark border-2 border-army/40 flex items-center justify-center">
                  <svg className="w-10 h-10 text-brand-yellow" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="text-white text-3xl font-black uppercase tracking-tight mb-2">Premium Content</h3>

                {!user ? (
                  // Logged-out user -- direct to login with return path
                  <>
                    <p className="text-gray-400 mb-8 text-lg">
                      {isFreeExclusive
                        ? 'Free exclusive -- sign in to watch'
                        : 'Sign in to unlock this video'}
                    </p>
                    <Link
                      to={`/login?redirect=${encodeURIComponent(`/video/${id}`)}`}
                      className="btn-primary text-lg !px-10 !py-4 inline-block"
                      data-testid="login-cta-link"
                    >
                      Sign in to watch
                    </Link>
                  </>
                ) : isFreeExclusive ? (
                  // Logged-in user on a free exclusive -- should not normally see this
                  // (canWatch should be true), but defensive fallback
                  <p className="text-green-400 mb-8 text-lg font-semibold">
                    Free &middot; Sign in to watch
                  </p>
                ) : (
                  // Logged-in user on a paid video (any status) -- show purchase CTA
                  <>
                    <p className="text-gray-400 mb-8 text-lg">Unlock this video to watch</p>
                    <button
                      onClick={handlePurchase}
                      disabled={purchasing || !video.price_cents}
                      className="btn-primary text-lg !px-10 !py-4 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="purchase-button"
                    >
                      {purchasing
                        ? 'Processing...'
                        : video.price
                          ? `Purchase for ${video.price}`
                          : 'Not available for purchase yet'}
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Video Info */}
        <div className="card p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h1 className="text-3xl md:text-4xl font-black text-white mb-2" data-testid="video-title">
                {video.title}
              </h1>
              {isExclusive && (
                <span className="inline-block bg-army/20 text-army-light px-3 py-1 rounded text-xs font-bold uppercase tracking-wider border border-army/30">
                  Exclusive
                </span>
              )}
            </div>

            <div className="ml-4 flex-shrink-0 flex items-center gap-3">
              {/* Edit button -- visible to admins only */}
              {checkIsAdmin() && !editOpen && (
                <button
                  onClick={openEditPanel}
                  data-testid="edit-video-button"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-800 border border-gray-700 text-gray-300 hover:text-white hover:border-gray-500 text-sm font-medium transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit
                </button>
              )}

              {/* Like Button */}
              {video.likes_count !== undefined && (
                <LikeButton
                  type="video"
                  id={video.id}
                  initialLikesCount={video.likes_count}
                  initialIsLiked={video.is_liked || false}
                  isAuthenticated={!!user}
                />
              )}
            </div>
          </div>

          {/* Edit Panel -- inline form for owner/admin */}
          {editOpen && (
            <div
              data-testid="edit-video-panel"
              className="mb-6 p-5 rounded-xl bg-gray-900 border border-army/30 space-y-4"
            >
              <h3 className="text-white font-bold text-lg">Edit Video</h3>

              {/* Title */}
              <div>
                <label className="block text-gray-400 text-xs font-medium uppercase tracking-wider mb-1">
                  Title
                </label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  data-testid="edit-title-input"
                  className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-brand-yellow/40 focus:border-transparent text-sm"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-gray-400 text-xs font-medium uppercase tracking-wider mb-1">
                  Description
                </label>
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  data-testid="edit-description-input"
                  rows={3}
                  className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-brand-yellow/40 focus:border-transparent text-sm"
                />
              </div>

              {/* Status */}
              <div>
                <label className="block text-gray-400 text-xs font-medium uppercase tracking-wider mb-1">
                  Access Level
                </label>
                <select
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value)}
                  data-testid="edit-status-select"
                  className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-brand-yellow/40 focus:border-transparent text-sm"
                >
                  <option value="exclusive">Premium (Purchase Required)</option>
                  <option value="public">Free (Public)</option>
                  <option value="unlisted">Unlisted</option>
                </select>
              </div>

              {/* Price -- shown for ALL statuses; blank = free (no purchase required) */}
              <div>
                <label className="block text-gray-400 text-xs font-medium uppercase tracking-wider mb-1">
                  Price (USD)
                  <span className="text-gray-600 font-normal ml-2 normal-case">-- minimum $0.50 &middot; leave blank for free</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                  <input
                    type="number"
                    value={editPrice}
                    onChange={(e) => setEditPrice(e.target.value)}
                    step="0.01"
                    min="0.50"
                    placeholder="Leave blank for free"
                    data-testid="edit-price-input"
                    className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg pl-7 pr-4 py-2.5 focus:ring-2 focus:ring-brand-yellow/40 focus:border-transparent text-sm"
                  />
                </div>
              </div>

              {/* Frame picker -- Mux videos only */}
              {video.platform === 'mux' && (
                <MuxThumbnailFramePicker
                  video={video}
                  thumbnailTime={editThumbnailTime}
                  onTimeChange={setEditThumbnailTime}
                />
              )}

              {/* Error */}
              {editError && (
                <p
                  data-testid="edit-error-message"
                  className="text-red-400 text-sm"
                >
                  {editError}
                </p>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-1">
                <button
                  onClick={handleEditSave}
                  disabled={editSaving}
                  data-testid="edit-save-button"
                  className="px-5 py-2 rounded-lg bg-army hover:bg-army-light disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-sm transition-colors"
                >
                  {editSaving ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  onClick={() => setEditOpen(false)}
                  disabled={editSaving}
                  data-testid="edit-cancel-button"
                  className="px-5 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 disabled:opacity-50 text-gray-300 hover:text-white font-semibold text-sm transition-colors border border-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Artists */}
          {renderArtists()}

          {/* Description */}
          {video.description && (
            <div>
              <h3 className="text-gray-500 text-xs uppercase tracking-widest mb-3">Description</h3>
              <p className="text-gray-400 leading-relaxed whitespace-pre-wrap">{video.description}</p>
            </div>
          )}
        </div>

        {/* Battle Voting */}
        {video.artists && video.artists.length >= 2 && (
          <BattleVoting videoId={video.id} artists={video.artists} />
        )}

        {/* Comments Section */}
        <CommentSection videoId={id} />
      </div>

      {/* Alert Modal */}
      <AlertModal
        show={alertModal.show}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ show: false, title: '', message: '', type: 'info' })}
      />
    </div>
  );
};

export default VideoDetail;
